<template>
    <div>
        <form
            class="mt-8 space-y-6 pb-24 md:pb-0"
            @change="form.errors.clear($event.target.name)"
            @keyup="form.errors.clear($event.target.name)"
            @submit.prevent="submit"
            enctype="multipart/form-data"
        >
            <p class="form-error" v-if="form.errors.has('general')" v-text="form.errors.get('general')"></p>

            <div>
                <label for="name" class="label">
                    {{ $t('Full name') }}
                    <span class="text-sm text-red-700">*</span>
                </label>
                <input type="text" name="name" id="name" v-model="form.name" class="input" />
                <p class="form-error" v-if="form.errors.has('name')" v-text="form.errors.get('name')"></p>
            </div>

            <div>
                <label for="email" class="label">
                    {{ $t('Email address') }}
                    <span class="text-sm text-red-700">*</span>
                </label>
                <input type="email" name="email" id="email" v-model="form.email" class="input" />
                <p class="form-error" v-if="form.errors.has('email')" v-text="form.errors.get('email')"></p>
            </div>

            <div>
                <label for="phone_number" class="label">
                    {{ $t('Phone number') }}
                    <span v-if="phoneRequired" class="text-sm text-red-700">*</span>
                </label>
                <input type="tel" name="phone_number" id="phone_number" v-model="form.phone_number" class="input" :required="phoneRequired" />
                <p class="form-error" v-if="form.errors.has('phone_number')" v-text="form.errors.get('phone_number')"></p>
            </div>

            <div v-if="props.model?.ask_for_canadian_status">
                <label for="canadian_status" class="label">
                    {{ $t('What is your Canadian status?') }}
                </label>
                <select name="canadian_status" id="canadian_status" v-model="form.canadian_status" class="input">
                    <option value="">{{ $t('Select an option') }}</option>
                    <option value="citizen">{{ $t('Canadian citizen') }}</option>
                    <option value="permanent_resident">{{ $t('Permanent resident') }}</option>
                    <option value="open_work_permit">{{ $t('Open work permit') }}</option>
                    <option value="closed_work_permit">{{ $t('Closed work permit') }}</option>
                    <option value="other">{{ $t('Other status') }}</option>
                    <option value="prefer_not_to_say">{{ $t('Prefer not to say') }}</option>
                </select>
                <p class="form-error" v-if="form.errors.has('canadian_status')" v-text="form.errors.get('canadian_status')"></p>
            </div>

            <CustomFormField
                v-for="field in model.custom_form_fields"
                :key="field.internal_id"
                :field="field"
                v-model="form[field.internal_id]"
            />

            <div>
                <label class="flex items-center space-x-4">
                    <Toggle v-model="form.newsletter" />
                    <span>
                        {{ $t('I wish to subscribe to the newsletter and SMS notifications from CAMAQ and its employability partners.') }}
                    </span>
                </label>
            </div>

            <div>
                <Button class="flex w-full justify-center lg:text-lg" :busy="form.processing">
                    <span v-if="isEvent">{{ $t('Confirm your presence') }}</span>
                    <span v-if="isFormation">{{ $t('Sign-up for the formation') }}</span>
                </Button>
            </div>
        </form>
    </div>
</template>

<script setup>
    import Form from '@/forms';
    import Toggle from '@/Shared/Toggle.vue';
    import Swal from 'sweetalert2';
    import { computed, onBeforeMount, reactive } from 'vue';
    import CustomFormField from './CustomFormField.vue';

    const emit = defineEmits(['success']);

    const props = defineProps({
        isEvent: Boolean,
        isFormation: Boolean,
        model: Object,
    });

    const phoneRequired = computed(() => props.isEvent && props.model?.id === 100165);

    let form = reactive(
        new Form({
            name: '',
            email: '',
            phone_number: '',
            canadian_status: '',
            newsletter: false,
        })
    );

    onBeforeMount(() => {
        props.model.custom_form_fields?.forEach(field => {
            form.add(field.internal_id, '');
        });
    });

    function submit() {
        let url = props.isEvent ? `/evenements/${props.model.slug}/inscription` : `/formations/${props.model.slug}/inscription`;

        form.post(url)
            .then(response => {
                emit('success');
            })
            .catch(error => {
                const status = error.response?.status;

                // Validation errors, already handled by the form
                if (status === 422) {
                    return;
                }

                if (status === 419) {
                    // CSRF token error, page expired (need refresh)
                    Swal.fire({
                        title: 'Page expirée',
                        text: 'La page est expirée, veuillez réactualiser la page et réessayer.',
                        icon: 'error',
                    });
                } else if (!error.response) {
                    // Network error, timeout, or CORS issue
                    Swal.fire({
                        title: 'Erreur de connexion',
                        text: 'Impossible de contacter le serveur. Veuillez vérifier votre connexion internet et réessayer.',
                        icon: 'error',
                    });
                } else {
                    Swal.fire({
                        title: 'Erreur',
                        text: "Une erreur s'est produite lors de l'application, veuillez réessayer. Si le problème persiste, veuillez contacter le support à support@camaq.ca",
                        icon: 'error',
                    });
                }

                throw new Error(
                    `Erreur inscription CAMAQ (status: ${status ?? 'N/A'} - statusText: ${error.response?.statusText ?? error.message ?? 'Erreur inconnue'})`
                );
            });
    }

    defineExpose({ form });
</script>
